import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CURRENCIES, formatMoney, parseRate, rateChangeIsLarge, type CurrencyCode } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Button, Checkbox, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Spinner, TextInput, fieldErrors, formatDateTime, formatRelative, useToast } from '../../ui';
import { useTillCurrencies, type TillCurrencies } from '../pos/PaymentDialog';

interface RateRow {
  id: string;
  currency: CurrencyCode;
  rate: string;
  source: string | null;
  effectiveFrom: string;
  setByName: string | null;
}

export function ExchangeRatesPage() {
  const { can } = useSession();
  const till = useTillCurrencies();
  const history = useQuery({ queryKey: ['exchange-rates'], queryFn: () => api.get<RateRow[]>('/api/exchange-rates') });
  const [setting, setSetting] = useState<TillCurrencies['currencies'][number] | null>(null);
  const base = till.data?.baseCurrency;
  const foreign = till.data?.currencies.filter((c) => c.currency !== base) ?? [];
  return (
    <>
      <PageHeader
        title="Exchange rates"
        description="The rates the till uses to take other currencies. A new rate applies to sales from the moment it is saved; earlier sales keep the rate they were made at."
      />
      {till.isLoading && <Spinner />}
      {till.error && <ErrorNotice error={till.error} />}
      {till.data && foreign.length === 0 && (
        <Panel>
          <EmptyState title={`Only ${base} is accepted`}>Add currencies such as ZiG or rand under Business settings to take them at the till.</EmptyState>
        </Panel>
      )}
      {foreign.length > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {foreign.map((c) => {
            const sample = 1000;
            return (
              <div key={c.currency} className="rounded-[var(--radius-panel)] border border-line bg-surface px-5 py-4">
                <p className="text-sm text-muted">{CURRENCIES[c.currency].name}</p>
                {c.rate ? (
                  <>
                    <p className="mt-1 text-2xl font-semibold num">
                      {base} 1 = {c.currency} {c.rate}
                    </p>
                    <p className="text-sm text-muted num">
                      {formatMoney(sample, base!)} is {formatMoney(Math.round((sample * c.rateMicros!) / 1_000_000), c.currency)} · set {formatRelative(c.effectiveFrom)}
                    </p>
                  </>
                ) : (
                  <p className="mt-1 font-medium text-warn">No rate yet: the till can't take {c.currency}.</p>
                )}
                {can('currency.rates_manage') && (
                  <Button size="sm" className="mt-3" onClick={() => setSetting(c)}>
                    {c.rate ? 'Update rate' : 'Set rate'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Panel title="Rate history">
        {history.isLoading && <Spinner />}
        {history.data?.length === 0 && <EmptyState title="No rates set yet" />}
        {!!history.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">From</th>
                  <th className="px-3 py-2.5 font-medium">Currency</th>
                  <th className="px-3 py-2.5 text-right font-medium">Rate per {base} 1</th>
                  <th className="px-3 py-2.5 font-medium">Source</th>
                  <th className="px-5 py-2.5 font-medium">Set by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {history.data.map((r) => (
                  <tr key={r.id}>
                    <td className="px-5 py-2.5 text-sm num">{formatDateTime(r.effectiveFrom)}</td>
                    <td className="px-3 py-2.5">{r.currency}</td>
                    <td className="px-3 py-2.5 text-right num">{r.rate}</td>
                    <td className="px-3 py-2.5 text-sm">{r.source ?? '—'}</td>
                    <td className="px-5 py-2.5 text-sm">{r.setByName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {setting && base && <RateDialog base={base} current={setting} onClose={() => setSetting(null)} />}
    </>
  );
}

function RateDialog({ base, current, onClose }: { base: CurrencyCode; current: TillCurrencies['currencies'][number]; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rate, setRate] = useState(current.rate ?? '');
  const [source, setSource] = useState('RBZ interbank');
  const [confirmed, setConfirmed] = useState(false);
  const micros = parseRate(rate);
  const large = micros != null && current.rateMicros != null && rateChangeIsLarge(current.rateMicros, micros);
  const m = useMutation({
    mutationFn: () => api.post('/api/exchange-rates', { currency: current.currency, rate, source }),
    onSuccess: () => {
      toast(`${current.currency} rate saved`);
      for (const k of ['till-currencies', 'exchange-rates']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${current.currency} rate`}
      description={`How many ${current.currency} make ${base} 1. The till uses it straight away.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={micros == null || (large && !confirmed)} busy={m.isPending} onClick={() => m.mutate()}>
            Save rate
          </Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={`${current.currency} per ${base} 1`} htmlFor="r-rate" error={f.rate ?? (rate && micros == null ? 'Enter a number such as 26.85' : undefined)}>
          <TextInput id="r-rate" inputMode="decimal" className="num" value={rate} onChange={(e) => { setRate(e.target.value); setConfirmed(false); }} />
        </Field>
        <Field label="Source" htmlFor="r-src" hint="Where the rate comes from">
          <TextInput id="r-src" value={source} onChange={(e) => setSource(e.target.value)} />
        </Field>
      </div>
      {micros != null && (
        <p className="mt-3 text-sm text-muted num">
          {formatMoney(1000, base)} = {formatMoney(Math.round((1000 * micros) / 1_000_000), current.currency)}
        </p>
      )}
      {large && (
        <div className="mt-3 rounded-[8px] bg-warn-soft px-3 py-2 text-sm text-warn">
          <p className="mb-2">That is more than a 20% change from {current.rate}. Check it isn't a typing mistake.</p>
          <Checkbox label="The new rate is correct" checked={confirmed} onChange={setConfirmed} />
        </div>
      )}
    </Dialog>
  );
}
